import { useEffect } from "react";

import { FlowVersion } from "src/api/flowTypes";
import { Operation } from "src/api/flowVersionUpdateIndex";
import { CustomConnectionNode } from "src/constants/NodeDataTypes";
import { NODE_TYPE } from "src/constants/NodeTypes";
import { useCanAuthoringEditFlowVersion } from "src/hooks/useCanAuthoringEditFlowVersion";
import { useIsFeatureFlagEnabled } from "src/hooks/useIsFeatureFlagEnabled";
import { FEATURE_FLAGS } from "src/router/featureFlags";
import { useGraphStore } from "src/store/StoreProvider";
import { hashObject } from "src/utils/hashObject";

/**
 * Keeps the response-schema snapshot on custom connection nodes in sync with
 * the flow's output schema.
 *
 * CC nodes with response validation enabled carry a copy of the output schema
 * in `config.response_validation_config.response_schema` (Connect validates
 * provider responses against it). The copy is written when the toggle is
 * enabled; this hook rewrites it whenever the output schema changes so the
 * snapshot never drifts. Writes are idempotent: nodes whose snapshot already
 * matches are skipped.
 */
export const useSyncResponseValidationSchemas = (
  flowVersion: FlowVersion,
): void => {
  const canEdit = useCanAuthoringEditFlowVersion();
  const graphStore = useGraphStore();
  const outputSchema = flowVersion.output_schema ?? null;
  // Response validation only exists behind the manifest-builder flag; when
  // it is off, stay fully inert — in particular, don't subscribe to graph
  // changes (the nodesArray read below re-renders the observer wrapper on
  // every node mutation).
  const isManifestBuilderFlagEnabled = useIsFeatureFlagEnabled(
    FEATURE_FLAGS.manifestBuilder,
  );
  const enabled = canEdit && isManifestBuilderFlagEnabled;
  // Observed so the sync re-runs once the graph has loaded (the wrapper
  // mounting this hook is a MobX observer).
  const nodesCount = enabled ? graphStore.nodesArray.length : 0;

  useEffect(() => {
    if (!enabled) return;

    // The common case — no CC node opted into response validation — exits
    // before any schema hashing.
    const activeNodes = graphStore.nodesArray.filter(
      (node): node is CustomConnectionNode =>
        node.type === NODE_TYPE.CUSTOM_CONNECTION_NODE &&
        !!(node as CustomConnectionNode).data.config.response_validation_config
          ?.active,
    );
    if (activeNodes.length === 0) return;

    const outputSchemaHash = hashObject(outputSchema);
    const driftedNodes = activeNodes.filter(
      (node) =>
        hashObject(
          node.data.config.response_validation_config?.response_schema ?? null,
        ) !== outputSchemaHash,
    );
    if (driftedNodes.length === 0) return;

    const nodes = Object.fromEntries(
      driftedNodes.map((node) => [
        node.id,
        {
          ...node,
          data: {
            ...node.data,
            config: {
              ...node.data.config,
              response_validation_config: {
                active: true,
                response_schema: outputSchema,
              },
            },
          },
        },
      ]),
    );

    void graphStore.updateFlowVersionGraphAndStore?.({
      flowVersionId: flowVersion.id,
      nodes,
      edges: {},
      groups: {},
      operation: Operation.EDIT_NODE,
    });
  }, [enabled, graphStore, flowVersion.id, outputSchema, nodesCount]);
};
